import { integer, text } from "#drizzle";

import {
  customType,
  sqliteTable,
  type SQLiteTableExtraConfigValue,
} from "@-/com.npmjs.drizzle-orm/sqlite-core";
import type { BuildColumns, ColumnBuilderBase } from "@-/com.npmjs.drizzle-orm";

import type { LowercaseDatabaseName } from "#drizzle";

// 1. Define a custom type for ISO Dates
const dateType = customType<{ data: string; driverData: string }>({
  dataType() {
    return "text"; // SQLite will still store this as a text column
  },
  fromDriver(value: unknown): string {
    if (typeof value !== "string") {
      throw new TypeError(`Expected string from SQLite, got ${typeof value}`);
    }
    // When reading from SQLite, return the string as-is
    return value;
  },
  toDriver(value: string): string {
    // Attempt to parse the incoming value
    const parsedDate = new Date(value);

    // Check if it's a valid date
    if (Number.isNaN(parsedDate.getTime())) {
      throw new TypeError(`Database Error: Invalid date string provided -> ${value}`);
    }

    // Optional: You can either strict-check a regex here,
    // OR just safely auto-convert it to a guaranteed ISO string.
    // Returning parsedDate.toISOString() guarantees the DB gets a perfect ISO format.
    return parsedDate.toISOString();
  },
});

export function date<const Name extends string>(name: LowercaseDatabaseName<Name>) {
  return dateType(name);
}

const boolType = customType<{ data: boolean; driverData: number }>({
  dataType() {
    return "integer"; // SQLite uses integers for booleans
  },
  fromDriver(value: unknown): boolean {
    // Convert back to a boolean when reading from SQLite
    return value === 1;
  },
  toDriver(value: boolean): number {
    // Strictly validate that the value is an actual boolean at runtime
    if (typeof value !== "boolean") {
      throw new TypeError(`Database Error: Expected a strict boolean, received -> ${typeof value}`);
    }

    // Convert to SQLite's expected format (1 for true, 0 for false)
    return value ? 1 : 0;
  },
});

export function bool<const Name extends string>(name: LowercaseDatabaseName<Name>) {
  return boolType(name);
}

const common = {
  createdAt: date("created_at").notNull(),
  deletedAt: date("deleted_at"),
  id: text("id").primaryKey(),
  updatedAt: date("updated_at"),
} as const;

export function crudTable<const Name extends string, T extends Record<string, ColumnBuilderBase>>(
  name: LowercaseDatabaseName<Name>,
  schema: T,
  extraConfig?: (
    self: BuildColumns<Name, typeof common & T, "sqlite">,
  ) => SQLiteTableExtraConfigValue[],
) {
  return sqliteTable(name, { ...common, ...schema }, extraConfig);
}

const contentCommon = {
  /** Intentionally NOT stable; we should NEVER rely on this value for matching */
  id: integer("id").primaryKey(),
} as const;

/**
 * Content info data lives in its own SQLite file (`content.db`) opened
 * via a dedicated connection — see `database/content`. It is therefore NOT
 * bound to any drizzle `Symbol.for("drizzle:Schema")` alias; SQL emitted for
 * these tables uses the bare table name. This avoids depending on SQLite's
 * ATTACH DATABASE feature, which keeps the data portable to backends like
 * Cloudflare D1 that don't support it.
 */
export function contentTable<
  const Name extends string,
  T extends Record<string, ColumnBuilderBase>,
>(
  name: LowercaseDatabaseName<Name>,
  schema: T,
  extraConfig?: (
    self: BuildColumns<Name, typeof contentCommon & T, "sqlite">,
  ) => SQLiteTableExtraConfigValue[],
) {
  // Read-only; doesn't carry the createdAt/updatedAt/deletedAt soft-delete columns.
  return sqliteTable(name, { ...contentCommon, ...schema }, extraConfig);
}
